type QueryValue = string | number | boolean | null | undefined;

function trimSegment(segment: string): string {
    return segment.replace(/^\/+|\/+$/g, '');
}

function buildPath(...segments: Array<string | null | undefined | false>): string {
    const normalized = segments
        .filter((segment): segment is string => typeof segment === 'string' && segment.trim().length > 0)
        .map((segment) => trimSegment(segment));

    return normalized.length > 0 ? `/${normalized.join('/')}` : '/';
}

function withQuery(path: string, query?: Record<string, QueryValue>): string {
    if (!query) {
        return path;
    }

    const searchParams = new URLSearchParams();

    Object.entries(query).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
            return;
        }

        searchParams.set(key, String(value));
    });

    const queryString = searchParams.toString();
    return queryString ? `${path}?${queryString}` : path;
}

export const STATIC_ROUTES = {
    LOGIN: '/login',
    FORGOT_PASSWORD: '/forgot-password',
    RESET_PASSWORD: '/reset-password',
    DASHBOARD: '/dashboard',
    SUPERADMIN_DASHBOARD: '/superadmin/dashboard',
    PROGRAMS: '/programs',
    PROGRAM_CREATE: '/programs/create',
    PROGRAM_SELECTION: '/programs/select',
    UNAUTHORIZED: '/unauthorized',
} as const;

export const PROGRAM_ROUTE_SEGMENTS = {
    DASHBOARD: 'dashboard',
    WORKSPACE: 'workspace',
    FORMS: 'forms',
    WORKFLOW: 'workflow',
    REPORTS: 'reports',
    ROLES: 'roles',
    USER_ASSIGNMENTS: 'user-assignments',
    NOTIFICATIONS: 'notifications',
    AUTOMATION_RULES: 'automation-rules',
    WORKFLOW_AUTOMATIONS: 'workflow-automations',
    COMMUNICATION_TEMPLATES: 'communication-templates',
    AUTOMATION_MONITOR: 'automation-monitor',
    UI_SETTINGS: 'ui-settings',
    LEGAL_PAGES: 'legal-pages',
    AUDIT_REPORT: 'audit-report',
    ASSISTANCE_SETTINGS: 'assistance-settings',
    SELF_REGISTRATION_SETTINGS: 'self-registration-settings',
    FINANCIAL_ASSISTANCE: 'financial-assistance',
    PLANS4MODEL: 'plans4model',
    HOSPITALS: 'hospitals',
    HCPS: 'hcps',
    PATIENTS: 'patients',
    CASES: 'cases',
    CREATE: 'create',
    EDIT: 'edit',
    UTILIZE: 'utilize',
    ROLE_FORM_WORKFLOW_MAPPING: 'role-form-workflow-mapping',
    PERMISSIONS_MATRIX: 'permissions-matrix',
    PERMISSION_AUDIT: 'audit/permissions',
    ROLE_COMPARISON: 'role-comparison',
    LOCATION_SETTINGS: 'location-settings',
    ENTITY_INHERITANCE: 'entity-inheritance',
    DOCUMENT_REQUIREMENTS: 'document-requirements',
    DOCUMENT_BUNDLES: 'document-bundles',
    COMPLIANCE_PACKS: 'compliance-packs',
    OPERATIONAL_DASHBOARD: 'operational-dashboard',
} as const;

export const PROGRAM_ROUTE_PATTERNS = {
    LANDING: '/programs/:programId',
    DASHBOARD: '/programs/:programId/dashboard',
    WORKSPACE: '/programs/:programId/workspace',
    FORMS: '/programs/:programId/forms',
    WORKFLOW: '/programs/:programId/workflow',
    WORKSPACE_CASES: '/programs/:programId/workspace/cases',
    ASSISTANCE_CASES: '/programs/:programId/financial-assistance/cases',
} as const;

function buildProgramPath(programId: string, ...segments: string[]): string {
    return buildPath('programs', programId, ...segments);
}

export function buildProgramRouteFromRelative(
    programId: string,
    relativeRoute?: string,
    fallbackRoute: string = PROGRAM_ROUTE_SEGMENTS.WORKSPACE,
): string {
    const normalized = trimSegment(relativeRoute || fallbackRoute);

    if (!normalized) {
        return buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.WORKSPACE);
    }

    return buildProgramPath(programId, ...normalized.split('/'));
}

export const appRoutes = {
    login: () => STATIC_ROUTES.LOGIN,
    forgotPassword: () => STATIC_ROUTES.FORGOT_PASSWORD,
    resetPassword: () => STATIC_ROUTES.RESET_PASSWORD,
    dashboard: () => STATIC_ROUTES.DASHBOARD,
    superadminDashboard: () => STATIC_ROUTES.SUPERADMIN_DASHBOARD,
    programs: () => STATIC_ROUTES.PROGRAMS,
    programCreate: () => STATIC_ROUTES.PROGRAM_CREATE,
    programSelection: () => STATIC_ROUTES.PROGRAM_SELECTION,
    unauthorized: () => STATIC_ROUTES.UNAUTHORIZED,
    legalPage: (pageKey: string) => buildPath('legal', pageKey),
};

export const programRoutes = {
    landing: (programId: string) => buildProgramPath(programId),
    dashboard: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.DASHBOARD),
    adminDashboard: (programId: string) => buildProgramPath(programId, 'admin-dashboard'),
    operationalDashboard: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.OPERATIONAL_DASHBOARD),
    workspace: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.WORKSPACE),
    scopedDashboard: (programId: string) => buildProgramPath(programId, 'scoped-dashboard'),
    hospitalDashboard: (programId: string) => buildProgramPath(programId, 'hospital-dashboard'),
    hcpDashboard: (programId: string) => buildProgramPath(programId, 'hcp-dashboard'),
    hcpPatientApprovals: (programId: string, query?: Record<string, QueryValue>) =>
        withQuery(buildProgramPath(programId, 'hcp-dashboard', 'patient-approvals'), query),
    hcpPatientApprovalReview: (programId: string, query?: Record<string, QueryValue>) =>
        withQuery(buildProgramPath(programId, 'hcp-dashboard', 'patient-approval-review'), query),
    patientRegistrationApprovals: (programId: string, query?: Record<string, QueryValue>) =>
        withQuery(buildProgramPath(programId, 'patient-registration-approvals'), query),
    patientRegistrationApprovalDetail: (programId: string, approvalId: string) =>
        buildProgramPath(programId, 'patient-registration-approvals', approvalId),
    patientDashboard: (programId: string) => buildProgramPath(programId, 'patient-dashboard'),
    pharmaDashboard: (programId: string) => buildProgramPath(programId, 'pharma-dashboard'),
    myWork: (programId: string) => buildProgramPath(programId, 'my-work'),
    hospitals: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.HOSPITALS),
    hcps: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.HCPS),
    patients: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.PATIENTS),
    hospitalDetail: (programId: string, hospitalId: string, query?: Record<string, QueryValue>) =>
        withQuery(
            buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.HOSPITALS, hospitalId, 'view'),
            query,
        ),
    hcpDetail: (programId: string, hospitalId: string, hcpId: string, query?: Record<string, QueryValue>) =>
        withQuery(
            buildProgramPath(
                programId,
                PROGRAM_ROUTE_SEGMENTS.HOSPITALS,
                hospitalId,
                PROGRAM_ROUTE_SEGMENTS.HCPS,
                hcpId,
                'view',
            ),
            query,
        ),
    patientDetail: (
        programId: string,
        hospitalId: string,
        hcpId: string,
        patientId: string,
        query?: Record<string, QueryValue>,
    ) =>
        withQuery(
            buildProgramPath(
                programId,
                PROGRAM_ROUTE_SEGMENTS.HOSPITALS,
                hospitalId,
                PROGRAM_ROUTE_SEGMENTS.HCPS,
                hcpId,
                PROGRAM_ROUTE_SEGMENTS.PATIENTS,
                patientId,
                'view',
            ),
            query,
        ),
    patient360: (programId: string, patientId: string, query?: Record<string, QueryValue>) =>
        withQuery(
            buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.PATIENTS, patientId, '360'),
            query,
        ),
    patientCycleTimeline: (programId: string, patientId: string) =>
        buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.PATIENTS, patientId, 'cycles'),
    workspaceCases: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.WORKSPACE, PROGRAM_ROUTE_SEGMENTS.CASES),
    workspaceCaseDetail: (programId: string, caseId: string) =>
        buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.WORKSPACE, PROGRAM_ROUTE_SEGMENTS.CASES, caseId),
    workspacePatientCases: (programId: string, hospitalId: string, hcpId: string, patientId: string) =>
        buildProgramPath(
            programId,
            PROGRAM_ROUTE_SEGMENTS.WORKSPACE,
            PROGRAM_ROUTE_SEGMENTS.PATIENTS,
            hospitalId,
            hcpId,
            patientId,
            PROGRAM_ROUTE_SEGMENTS.CASES,
        ),
    workspacePatientCaseDetail: (programId: string, hospitalId: string, hcpId: string, patientId: string, caseId: string) =>
        buildProgramPath(
            programId,
            PROGRAM_ROUTE_SEGMENTS.WORKSPACE,
            PROGRAM_ROUTE_SEGMENTS.PATIENTS,
            hospitalId,
            hcpId,
            patientId,
            PROGRAM_ROUTE_SEGMENTS.CASES,
            caseId,
        ),
    workspacePatientCaseUtilization: (
        programId: string,
        hospitalId: string,
        hcpId: string,
        patientId: string,
        caseId: string,
        utilizationType: string,
    ) =>
        buildProgramPath(
            programId,
            PROGRAM_ROUTE_SEGMENTS.WORKSPACE,
            PROGRAM_ROUTE_SEGMENTS.PATIENTS,
            hospitalId,
            hcpId,
            patientId,
            PROGRAM_ROUTE_SEGMENTS.CASES,
            caseId,
            PROGRAM_ROUTE_SEGMENTS.UTILIZE,
            utilizationType,
        ),
    // ── Unified runtime form routes (single entry point) ──────────────────
    // All create/edit forms route through the universal RuntimeFormPage at
    //   /programs/:programId/runtime/:context/:mode[/:entityId]
    // Context keys are lowercase (`hospital`, `hcp`, `patient`, `pharmacy`,
    // `scp`, `callLog`, `dispenseCase`); mode is `create` | `edit`.
    //
    // Legacy `entityFormCreate`/`entityFormEdit` and `workflowFormCreate`/
    // `workflowFormEdit` names are retained as thin aliases that emit the
    // runtime URL. New code should prefer `runtimeNavigation.goToCreateEntity`
    // / `goToEditEntity` (frontend/src/modules/programs/runtime/runtimeNavigation.ts).
    entityFormCreate: (
        programId: string,
        entityType: 'hospital' | 'hcp' | 'patient' | 'pharmacy',
        query?: { hospitalId?: string; hcpId?: string },
    ) =>
        withQuery(
            buildProgramPath(programId, 'runtime', entityType, 'create'),
            query,
        ),
    entityFormEdit: (
        programId: string,
        entityType: 'hospital' | 'hcp' | 'patient' | 'pharmacy',
        entityId: string,
        query?: { hospitalId?: string; hcpId?: string },
    ) =>
        withQuery(
            buildProgramPath(programId, 'runtime', entityType, 'edit', entityId),
            query,
        ),

    workflowFormCreate: (
        programId: string,
        entityType: 'hospital' | 'hcp' | 'patient' | 'pharmacy',
        query?: { hospitalId?: string; hcpId?: string },
    ) =>
        withQuery(
            buildProgramPath(programId, 'runtime', entityType, 'create'),
            query,
        ),
    workflowFormEdit: (
        programId: string,
        entityType: 'hospital' | 'hcp' | 'patient' | 'pharmacy',
        entityId: string,
        query?: { hospitalId?: string; hcpId?: string },
    ) =>
        withQuery(
            buildProgramPath(programId, 'runtime', entityType, 'edit', entityId),
            query,
        ),
    workspaceAssistanceCreate: (programId: string, hospitalId: string, hcpId: string, patientId: string) =>
        buildProgramPath(
            programId,
            PROGRAM_ROUTE_SEGMENTS.WORKSPACE,
            PROGRAM_ROUTE_SEGMENTS.PATIENTS,
            hospitalId,
            hcpId,
            patientId,
            'assistance',
            PROGRAM_ROUTE_SEGMENTS.CREATE,
        ),
    workspaceHospitalEdit: (programId: string, hospitalId: string) =>
        buildProgramPath(
            programId,
            PROGRAM_ROUTE_SEGMENTS.WORKSPACE,
            PROGRAM_ROUTE_SEGMENTS.HOSPITALS,
            hospitalId,
            PROGRAM_ROUTE_SEGMENTS.EDIT,
        ),
    workspaceHcpEdit: (programId: string, hospitalId: string, hcpId: string) =>
        buildProgramPath(
            programId,
            PROGRAM_ROUTE_SEGMENTS.WORKSPACE,
            PROGRAM_ROUTE_SEGMENTS.HCPS,
            hospitalId,
            hcpId,
            PROGRAM_ROUTE_SEGMENTS.EDIT,
        ),
    workspacePatientEdit: (programId: string, hospitalId: string, hcpId: string, patientId: string) =>
        buildProgramPath(
            programId,
            PROGRAM_ROUTE_SEGMENTS.WORKSPACE,
            PROGRAM_ROUTE_SEGMENTS.PATIENTS,
            hospitalId,
            hcpId,
            patientId,
            PROGRAM_ROUTE_SEGMENTS.EDIT,
        ),
    forms: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.FORMS),
    formBuilder: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.FORMS, 'builder'),
    formEdit: (programId: string, formId: string) =>
        buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.FORMS, formId, PROGRAM_ROUTE_SEGMENTS.EDIT),
    formBuilderDetail: (programId: string, formId: string) =>
        buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.FORMS, formId, 'builder'),
    workflow: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.WORKFLOW),
    autoHealing: (programId: string) => buildProgramPath(programId, 'auto-healing'),
    adaptiveWorkflows: (programId: string) => buildProgramPath(programId, 'adaptive-workflows'),
    workflowTemplates: (programId: string) => buildProgramPath(programId, 'workflow-templates'),
    roles: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.ROLES),
    roleCreate: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.ROLES, PROGRAM_ROUTE_SEGMENTS.CREATE),
    roleEdit: (programId: string, roleId: string) =>
        buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.ROLES, roleId, PROGRAM_ROUTE_SEGMENTS.EDIT),
    roleCategories: (programId: string) => buildProgramPath(programId, 'role-categories'),
    roleCategoryCreate: (programId: string) => buildProgramPath(programId, 'role-categories', PROGRAM_ROUTE_SEGMENTS.CREATE),
    roleCategoryEdit: (programId: string, categoryId: string) =>
        buildProgramPath(programId, 'role-categories', categoryId, PROGRAM_ROUTE_SEGMENTS.EDIT),
    listFieldVisibility: (programId: string) => buildProgramPath(programId, 'list-field-visibility'),
    userAssignments: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.USER_ASSIGNMENTS),
    userAssignmentCreate: (programId: string) =>
        buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.USER_ASSIGNMENTS, PROGRAM_ROUTE_SEGMENTS.CREATE),
    userAssignmentEdit: (programId: string, assignmentId: string) =>
        buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.USER_ASSIGNMENTS, assignmentId, PROGRAM_ROUTE_SEGMENTS.EDIT),
    notifications: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.NOTIFICATIONS),
    automationRules: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.AUTOMATION_RULES),
    automationRuleReviewQueue: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.AUTOMATION_RULES, 'review-queue'),
    automationRuleDetail: (programId: string, ruleId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.AUTOMATION_RULES, ruleId),
    workflowAutomations: (programId: string, workflowId: string) =>
        buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.WORKFLOW_AUTOMATIONS, workflowId),
    communicationTemplates: (programId: string) =>
        buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.COMMUNICATION_TEMPLATES),
    automationMonitor: (programId: string) =>
        buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.AUTOMATION_MONITOR),
    uiSettings: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.UI_SETTINGS),
    notificationsInbox: (programId: string) => buildProgramPath(programId, 'notifications', 'inbox'),
    communications: (programId: string) => buildProgramPath(programId, 'communications'),
    legalPages: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.LEGAL_PAGES),
    legalPage: (programId: string, pageKey: string) => buildProgramPath(programId, 'legal', pageKey),
    auditReport: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.AUDIT_REPORT),
    permissionsMatrix: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.PERMISSIONS_MATRIX),
    permissionAudit: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.PERMISSION_AUDIT),
    roleComparison: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.ROLE_COMPARISON),
    locationSettings: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.LOCATION_SETTINGS),
    entityInheritance: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.ENTITY_INHERITANCE),
    documentRequirements: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.DOCUMENT_REQUIREMENTS),
    assistanceSettings: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.ASSISTANCE_SETTINGS),
    selfRegistrationSettings: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.SELF_REGISTRATION_SETTINGS),
    benefitPlans: (programId: string) => buildProgramPath(programId, 'benefit-plans'),
    benefitPlanCreate: (programId: string) => buildProgramPath(programId, 'benefit-plans', PROGRAM_ROUTE_SEGMENTS.CREATE),
    benefitPlanDetail: (programId: string, benefitPlanId: string) => buildProgramPath(programId, 'benefit-plans', benefitPlanId),
    benefitPlanEdit: (programId: string, benefitPlanId: string) =>
        buildProgramPath(programId, 'benefit-plans', benefitPlanId, PROGRAM_ROUTE_SEGMENTS.EDIT),
    plans4Model: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.PLANS4MODEL),
    plans4ModelCreate: (programId: string, modelType: string) =>
        buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.PLANS4MODEL, PROGRAM_ROUTE_SEGMENTS.CREATE, modelType),
    plans4ModelDetail: (programId: string, planId: string) =>
        buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.PLANS4MODEL, planId),
    plans4ModelEdit: (programId: string, planId: string) =>
        buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.PLANS4MODEL, planId, PROGRAM_ROUTE_SEGMENTS.EDIT),
    assistanceCases: (programId: string) =>
        buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.FINANCIAL_ASSISTANCE, PROGRAM_ROUTE_SEGMENTS.CASES),
    caseUtilization: (programId: string, caseId: string, utilizationType: string) =>
        buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.CASES, caseId, PROGRAM_ROUTE_SEGMENTS.UTILIZE, utilizationType),
    assistanceClaims: (programId: string) => buildProgramPath(programId, 'assistance-claims'),
    assistanceClaimCreate: (programId: string) => buildProgramPath(programId, 'assistance-claims', 'new'),
    assistanceClaimDetail: (programId: string, claimId: string) =>
        buildProgramPath(programId, 'assistance-claims', claimId),
    callLogs: (programId: string) => buildProgramPath(programId, 'call-logs'),
    callLogCreate: (programId: string) => buildProgramPath(programId, 'call-logs', 'new'),
    callLogDetail: (programId: string, callLogId: string) => buildProgramPath(programId, 'call-logs', callLogId),
    callLogSettings: (programId: string) => buildProgramPath(programId, 'call-log-settings'),
    reports: (programId: string) => buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.REPORTS),
    demoGuide: (programId: string) => buildProgramPath(programId, 'demo-guide'),
    insights: (programId: string) => buildProgramPath(programId, 'insights'),
    roleFormWorkflowMapping: (programId: string) =>
        buildProgramPath(programId, PROGRAM_ROUTE_SEGMENTS.ROLE_FORM_WORKFLOW_MAPPING),
    cycleRequestQueue: (programId: string, query?: Record<string, QueryValue>) =>
        withQuery(buildProgramPath(programId, 'workspace', 'cycle-requests'), query),
};